// ── WordSlots — игровой автомат со словами («Аркада», боковое меню) ──────────
// Для красоты и чуть-чуть для памяти: три барабана с мастями и семёркой, рамка из лампочек
// (data-foil, как вывеска SHOP), кнопка «Крутить». Барабаны останавливаются по очереди, а
// автомат «выдаёт» слово из карточек изучаемого языка — с переводом. Три одинаковых символа —
// джекпот (примерно раз в 12 прокруток): лампочки вспыхивают золотом и бегут быстрее,
// барабаны светятся, мигает «ДЖЕКПОТ!», сыплются монеты со звоном (celebrate). Два
// одинаковых — «Почти!». «Меньше анимации» — без прокрутки, сразу результат.
//
//   <WordSlots C={C} words={[{ word, translation }]} />  — SideNav, только в теме с C.face

import { useEffect, useRef, useState } from 'react';
import { cardSound, celebrate } from '../arcadeJuice';
import { t } from '../i18n';
import { Button } from '../ui';
import { panelSurface } from '../ui/surface';

const SYMBOLS = ['♥', '♠', '♦', '♣', '7', '★'];
// «Колода» (по макету): вместо семёрки — иероглиф 七 («семь»), спокойные цвета.
const DECK_SYMBOLS = ['七', '★', '♦', '♥', '♠', '♣'];
const COLORS = { '♥': '#e0342b', '♦': '#e0342b', '♠': '#1d1b26', '♣': '#1d1b26', 7: '#e08a00', '★': '#1f7fd8', '七': '#1d2b2c' };
const STOP_MS = [600, 900, 1200];
const JACKPOT_BOOST = 0.06; // сверх честных 1/36 — чтобы джекпот иногда всё же случался
const JACKPOT_MS = 3500;
const reduced = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const pickOne = (list) => list[Math.floor(Math.random() * list.length)];

// variant: 'arcade' — кабинет с лампочками в боковом меню; 'deck' — панель «Колоды» по
// макету: «Слоты / Бонусный опыт / +XP», кремовые барабаны в золотой рамке, золотая кнопка.
export default function WordSlots({ C, words, style, variant = 'arcade' }) {
  const deck = variant === 'deck';
  const symbols = deck ? DECK_SYMBOLS : SYMBOLS;
  const [reels, setReels] = useState(deck ? ['七', '★', '七'] : ['7', '7', '7']);
  const [spinning, setSpinning] = useState(false);
  const [prize, setPrize] = useState(null);
  const [win, setWin] = useState(''); // '' | 'near' | 'jackpot'
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  if (!words?.length) return null;

  const spin = () => {
    if (spinning) return;
    const goal = [pickOne(symbols), pickOne(symbols), pickOne(symbols)];
    if (Math.random() < JACKPOT_BOOST) goal[1] = goal[2] = goal[0];
    const word = pickOne(words);
    setPrize(null);
    setWin('');
    const finish = () => {
      setSpinning(false);
      setPrize(word);
      if (goal[0] === goal[1] && goal[1] === goal[2]) {
        setWin('jackpot');
        celebrate(true, t("Джекпот!"));
        timers.current.push(setTimeout(() => setWin(''), JACKPOT_MS));
      } else if (goal[0] === goal[1] || goal[1] === goal[2] || goal[0] === goal[2]) setWin('near');
    };
    if (reduced()) { setReels(goal); finish(); return; }
    setSpinning(true);
    const start = performance.now();
    const tick = () => {
      const el = performance.now() - start;
      setReels((r) => r.map((s, i) => (el >= STOP_MS[i] ? goal[i] : pickOne(symbols))));
      if (el < STOP_MS[2]) timers.current.push(setTimeout(tick, 70));
      else finish();
    };
    STOP_MS.forEach((ms) => timers.current.push(setTimeout(() => cardSound('deal'), ms)));
    tick();
  };

  const face = C.face;
  const result = (
    <div role="status" style={{ minHeight: deck ? 0 : 40, marginBottom: deck && !prize && !spinning ? 0 : 8, fontSize: 13, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
      {prize ? (
        <>
          <div style={{ fontWeight: 700, color: C.text }}>{prize.word}</div>
          <div style={{ color: C.mid, fontSize: 12 }}>{prize.translation}</div>
        </>
      ) : deck ? (spinning ? <span style={{ color: C.faint, fontSize: 12 }}>{t("Крутится…")}</span> : null)
        : <span style={{ color: C.faint, fontSize: 12 }}>{spinning ? t("Крутится…") : t("Крутите — выпадет слово")}</span>}
    </div>
  );
  if (deck) {
    const gold = C.gold || C.warm;
    return (
      <div data-panel="" data-jackpot={win === 'jackpot' ? '' : undefined} style={{ ...panelSurface(C), borderRadius: 10, padding: '12px 12px 12px', boxSizing: 'border-box', ...style }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
          <span style={{ minWidth: 0 }}>
            <span data-jackpot-title={win === 'jackpot' ? '' : undefined} style={{ display: 'block', fontSize: 15, fontWeight: 600, color: C.text }}>
              {win === 'jackpot' ? t("Джекпот!") : win === 'near' ? t("Почти!") : t("Слоты")}
            </span>
            <span style={{ display: 'block', fontSize: 11.5, color: C.faint }}>{t("Бонусный опыт")}</span>
          </span>
          <span data-display="" style={{ fontSize: 14, color: gold }}>+XP</span>
        </div>
        <div aria-hidden="true" style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 10 }}>
          {reels.map((s, i) => (
            <span key={i} data-reel="" data-serif="" style={{ flex: '1 1 0', height: 40, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: face.bg, border: `2px solid ${gold}`, color: COLORS[s] || face.text, fontSize: 20, lineHeight: 1 }}>{s}</span>
          ))}
        </div>
        {result}
        <button type="button" onClick={spin} disabled={spinning} className="ac-press"
          style={{ width: '100%', height: 32, borderRadius: 4, border: 'none', background: gold, color: '#14262a', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
            cursor: spinning ? 'default' : 'pointer', opacity: spinning ? 0.7 : 1 }}>{t("Крутить")}</button>
      </div>
    );
  }
  return (
    <div data-foil="" data-jackpot={win === 'jackpot' ? '' : undefined}
      style={{ margin: '14px 6px 6px', padding: '10px 10px 12px', borderRadius: 12, background: 'rgba(10,12,28,.85)', textAlign: 'center', ...style }}>
      <div data-display="" data-jackpot-title={win === 'jackpot' ? '' : undefined}
        style={{ fontSize: 15, color: '#ffd24a', letterSpacing: '.06em', marginBottom: 8, textShadow: '0 0 8px rgba(255,190,60,.8)' }}>
        {win === 'jackpot' ? t("Джекпот!") : win === 'near' ? t("Почти!") : t("Слоты")}
      </div>
      <div aria-hidden="true" style={{ display: 'flex', gap: 5, justifyContent: 'center', marginBottom: 8 }}>
        {reels.map((s, i) => (
          <span key={i} data-display="" data-reel="" style={{ width: 44, height: 50, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: face.bg, border: `2px solid ${face.text}`, boxShadow: `inset 0 -8px 10px -6px rgba(0,0,0,.25), 0 2px 0 ${face.text}`,
            color: COLORS[s], fontSize: 24, lineHeight: 1, textShadow: 'none' }}>{s}</span>
        ))}
      </div>
      {result}
      <Button C={C} variant="primary" size="sm" full onClick={spin} disabled={spinning}>{t("Крутить")}</Button>
    </div>
  );
}
